import React from 'react';
import {
  Card as MuiCard,
  CardContent,
  CardActions,
  CardHeader,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Skeleton,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

interface CardProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  loading?: boolean;
  elevation?: number;
  sx?: SxProps<Theme>;
  headerAction?: React.ReactNode;
  status?: 'success' | 'warning' | 'error' | 'info';
  progress?: number;
}

export const Card: React.FC<CardProps> = ({
  title,
  subtitle,
  children,
  actions,
  loading = false,
  elevation = 1,
  sx,
  headerAction,
  status,
  progress,
}) => {
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <MuiCard elevation={elevation} sx={sx}>
        <CardContent>
          <Skeleton variant="text" height={32} width="60%" />
          <Skeleton variant="text" height={20} width="40%" sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={120} />
        </CardContent>
      </MuiCard>
    );
  }

  return (
    <MuiCard
      elevation={elevation}
      sx={{
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          elevation: elevation + 1,
          transform: 'translateY(-2px)',
        },
        ...sx,
      }}
    >
      {(title || subtitle || headerAction) && (
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" component="h2">
                {title}
              </Typography>
              {status && (
                <Chip
                  label={status}
                  color={getStatusColor(status) as any}
                  size="small"
                />
              )}
            </Box>
          }
          subheader={subtitle}
          action={headerAction}
          sx={{ pb: 1 }}
        />
      )}

      {progress !== undefined && (
        <Box sx={{ px: 2, pb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      <CardContent sx={{ pt: title || subtitle ? 0 : 2 }}>
        {children}
      </CardContent>

      {actions && (
        <CardActions sx={{ pt: 0 }}>
          {actions}
        </CardActions>
      )}
    </MuiCard>
  );
};

// Card specializzate per casi d'uso comuni
export const StatCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  trend?: {
    value: number;
    isPositive: boolean;
  };
}> = ({ title, value, subtitle, icon, color = 'primary', trend }) => {
  return (
    <Card
      title={title}
      subtitle={subtitle}
      sx={{
        textAlign: 'center',
        '&:hover': {
          transform: 'scale(1.02)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        {icon && (
          <Box sx={{ color: `${color}.main`, fontSize: 48 }}>
            {icon}
          </Box>
        )}
        <Typography variant="h3" component="div" color={`${color}.main`} fontWeight="bold">
          {value}
        </Typography>
        {trend && (
          <Typography
            variant="body2"
            color={trend.isPositive ? 'success.main' : 'error.main'}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            {trend.isPositive ? '↗' : '↘'} {Math.abs(trend.value)}%
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export const ActionCard: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
  disabled?: boolean;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}> = ({ title, description, icon, disabled = false, color = 'primary' }) => {
  return (
    <Card
      sx={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        '&:hover': disabled ? {} : {
          bgcolor: `${color}.50`,
          borderColor: `${color}.main`,
        },
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: `${color}.100`,
            color: `${color}.main`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

export const StatusCard: React.FC<{
  title: string;
  status: 'online' | 'offline' | 'syncing' | 'error';
  message: string;
  details?: string;
}> = ({ title, status, message, details }) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'online':
        return { color: 'success', icon: '🟢' };
      case 'offline':
        return { color: 'warning', icon: '🟡' };
      case 'syncing':
        return { color: 'info', icon: '🔄' };
      case 'error':
        return { color: 'error', icon: '🔴' };
      default:
        return { color: 'default', icon: '⚪' };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Card
      title={title}
      status={config.color as any}
      sx={{
        borderLeft: 4,
        borderLeftColor: `${config.color}.main`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h4">{config.icon}</Typography>
        <Box>
          <Typography variant="body1" fontWeight="medium">
            {message}
          </Typography>
          {details && (
            <Typography variant="body2" color="text.secondary">
              {details}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

